import { authStore } from '@/core/auth/authStore';
import { env } from '@/core/config/env';
import type {
  AssignLeadRequest,
  CreateLeadRequest,
  DuplicateCheckRequest,
  DuplicateCheckResponse,
  Lead,
  LeadListQuery,
  LeadWithEtag,
  PagedResponse,
  UpdateLeadRequest,
  QualifyLeadRequest,
  DisqualifyLeadRequest,
  ConvertLeadRequest,
  LeadConversionResponse,
  LeadStatusHistory,
  LeadScoringSettings,
  LeadSlaStatus,
  LeadEngagement,
  LeadEngagementEventType,
  LeadCaptureForm,
  PublicLeadCaptureForm,
  SubmitPublicLeadCaptureRequest,
} from '@/core/types/lead';
import { createUuid } from '@/core/utils/uuid';
import { mockLeadsApi } from '@/mock/mockAdapter';
import { resolveResponseEtag, toIfMatchHeader } from './etag';
import { httpClient } from './httpClient';

const requireAccessToken = (): string => {
  const token = authStore.getState().accessToken;
  if (!token) throw new Error('Access token is missing.');
  return token;
};

const buildQuery = (query: LeadListQuery): string => {
  const params = new URLSearchParams();
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.status) params.set('status', `${query.status.charAt(0).toUpperCase()}${query.status.slice(1)}`);
  if (query.ownerMembershipId) params.set('ownerMembershipId', query.ownerMembershipId);
  if (query.priority) params.set('priority', `${query.priority.charAt(0).toUpperCase()}${query.priority.slice(1)}`);
  if (query.unassigned !== undefined) params.set('unassigned', String(query.unassigned));
  if (query.source?.trim()) params.set('source', query.source.trim());
  if (query.minimumScore !== undefined) params.set('minimumScore', String(query.minimumScore));
  if (query.createdFromUtc) params.set('createdFromUtc', query.createdFromUtc);
  if (query.createdToUtc) params.set('createdToUtc', query.createdToUtc);
  if (query.sortBy) params.set('sortBy', query.sortBy);
  if (query.sortDirection) params.set('sortDirection', query.sortDirection);
  if (query.search?.trim()) params.set('search', query.search.trim());
  const result = params.toString();
  return result ? `?${result}` : '';
};

export const leadsApi = {
  async list(query: LeadListQuery = {}): Promise<PagedResponse<Lead>> {
    if (env.isDemo) return mockLeadsApi.list(requireAccessToken(), query);
    return (await httpClient.get<PagedResponse<Lead>>(`/api/v1/leads${buildQuery(query)}`)).data;
  },

  async get(leadId: string): Promise<LeadWithEtag> {
    if (env.isDemo) return mockLeadsApi.get(requireAccessToken(), leadId);
    const response = await httpClient.get<Lead>(`/api/v1/leads/${leadId}`);
    return {
      lead: response.data,
      etag: resolveResponseEtag(response.headers, response.data.rowVersion),
    };
  },

  async duplicateCheck(request: DuplicateCheckRequest): Promise<DuplicateCheckResponse> {
    if (env.isDemo) return mockLeadsApi.duplicateCheck(requireAccessToken(), request);
    return (await httpClient.post<DuplicateCheckResponse>('/api/v1/leads/duplicate-check', request)).data;
  },

  async create(request: CreateLeadRequest, idempotencyKey = createUuid()): Promise<LeadWithEtag> {
    if (env.isDemo) return mockLeadsApi.create(requireAccessToken(), request, idempotencyKey);
    const response = await httpClient.post<Lead>('/api/v1/leads', request, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    return {
      lead: response.data,
      etag: resolveResponseEtag(response.headers, response.data.rowVersion),
    };
  },

  async update(leadId: string, request: UpdateLeadRequest, etag: string): Promise<LeadWithEtag> {
    if (env.isDemo) return mockLeadsApi.update(requireAccessToken(), leadId, request, etag);
    const response = await httpClient.patch<Lead>(`/api/v1/leads/${leadId}`, request, {
      headers: { 'If-Match': toIfMatchHeader(etag) },
    });
    return {
      lead: response.data,
      etag: resolveResponseEtag(response.headers, response.data.rowVersion),
    };
  },

  async assign(leadId: string, request: AssignLeadRequest, etag: string): Promise<LeadWithEtag> {
    if (env.isDemo) return mockLeadsApi.assign(requireAccessToken(), leadId, request, etag);
    const response = await httpClient.post<Lead>(`/api/v1/leads/${leadId}/assign`, request, {
      headers: { 'If-Match': toIfMatchHeader(etag) },
    });
    return {
      lead: response.data,
      etag: resolveResponseEtag(response.headers, response.data.rowVersion),
    };
  },

  async qualify(leadId: string, request: QualifyLeadRequest): Promise<Lead> {
    if (env.isDemo) return mockLeadsApi.qualify(requireAccessToken(), leadId, request);
    return (await httpClient.post<Lead>(`/api/v1/leads/${leadId}/qualify`, request)).data;
  },

  async disqualify(leadId: string, request: DisqualifyLeadRequest): Promise<Lead> {
    if (env.isDemo) return mockLeadsApi.disqualify(requireAccessToken(), leadId, request);
    return (await httpClient.post<Lead>(`/api/v1/leads/${leadId}/disqualify`, request)).data;
  },

  async convert(leadId: string, request: ConvertLeadRequest, idempotencyKey = createUuid()): Promise<LeadConversionResponse> {
    if (env.isDemo) return mockLeadsApi.convert(requireAccessToken(), leadId, request);
    return (await httpClient.post<LeadConversionResponse>(`/api/v1/leads/${leadId}/convert`, request, { headers: { 'Idempotency-Key': idempotencyKey } })).data;
  },

  async history(leadId: string): Promise<LeadStatusHistory[]> {
    if (env.isDemo) return mockLeadsApi.history(requireAccessToken(), leadId);
    return (await httpClient.get<LeadStatusHistory[]>(`/api/v1/leads/${leadId}/status-history`)).data;
  },

  async scoringSettings(): Promise<LeadScoringSettings> {
    return (await httpClient.get<LeadScoringSettings>('/api/v1/leads/scoring/settings')).data;
  },

  async updateScoringSettings(request: LeadScoringSettings): Promise<LeadScoringSettings> {
    return (await httpClient.request<LeadScoringSettings>('/api/v1/leads/scoring/settings', { method: 'PUT', body: request })).data;
  },

  async recalculateScores(): Promise<{ recalculated: number }> {
    return (await httpClient.post<{ recalculated: number }>('/api/v1/leads/scoring/recalculate', {})).data;
  },

  async sla(): Promise<LeadSlaStatus> {
    return (await httpClient.get<LeadSlaStatus>('/api/v1/leads/sla')).data;
  },

  async engagements(leadId: string): Promise<LeadEngagement[]> {
    return (await httpClient.get<LeadEngagement[]>(`/api/v1/leads/${leadId}/engagements`)).data;
  },

  async recordEngagement(leadId: string, eventType: LeadEngagementEventType, note?: string): Promise<Lead> {
    return (await httpClient.post<Lead>(`/api/v1/leads/${leadId}/engagements`, { eventType, note: note || null })).data;
  },

  async startFollowUp(leadId: string, note?: string): Promise<Lead> {
    return (await httpClient.post<Lead>(`/api/v1/leads/${leadId}/start-follow-up`, { eventType: 'contacted', note: note || null })).data;
  },

  async captureForms(): Promise<LeadCaptureForm[]> {
    return (await httpClient.get<LeadCaptureForm[]>('/api/v1/leads/capture-forms')).data;
  },

  async createCaptureForm(request: Pick<LeadCaptureForm, 'title' | 'source' | 'description' | 'defaultPriority'>): Promise<LeadCaptureForm> {
    return (await httpClient.post<LeadCaptureForm>('/api/v1/leads/capture-forms', request)).data;
  },

  async updateCaptureForm(formId: string, request: Pick<LeadCaptureForm, 'title' | 'source' | 'description' | 'defaultPriority' | 'isActive'>): Promise<LeadCaptureForm> {
    return (await httpClient.patch<LeadCaptureForm>(`/api/v1/leads/capture-forms/${formId}`, request)).data;
  },

  async publicCaptureForm(code: string): Promise<PublicLeadCaptureForm> {
    return (await httpClient.get<PublicLeadCaptureForm>(`/api/v1/public/lead-capture/${encodeURIComponent(code)}`, { authenticated: false })).data;
  },

  async submitPublicCaptureForm(code: string, request: SubmitPublicLeadCaptureRequest): Promise<{ accepted: boolean; message: string }> {
    return (await httpClient.post<{ accepted: boolean; message: string }>(`/api/v1/public/lead-capture/${encodeURIComponent(code)}`, request, { authenticated: false, retryOnUnauthorized: false })).data;
  },
};
